import type { KeyboardEvent, MouseEvent } from "react";
import { Play } from "lucide-react";
import BilingualText from "./BilingualText";
import {
  categoryById,
  categoryColors,
  difficultyScale,
  type LibraryCredential,
} from "./catalog";
import { localizedText } from "./localizedText";
import type { RoadmapLanguage } from "./types";

type CredentialLibraryCardProps = {
  credential: LibraryCredential;
  flipped: boolean;
  hasVideo: boolean;
  onToggle: () => void;
  onPlay: () => void;
  language: RoadmapLanguage;
};

const CredentialLibraryCard = ({
  credential,
  flipped,
  hasVideo,
  onToggle,
  onPlay,
  language,
}: CredentialLibraryCardProps) => {
  const title = localizedText(credential.name, language);
  const primaryCategory = categoryById.get(credential.primaryCategoryId);
  const categoryColor =
    categoryColors[credential.primaryCategoryId] ?? "#0C3052";
  const difficulty = difficultyScale[`${credential.difficultyScore}`];
  const categoryLabel = primaryCategory?.[
    language === "ar" ? "labelAr" : "labelEn"
  ];
  const difficultyLabel =
    difficulty[language === "ar" ? "labelAr" : "labelEn"];
  const text =
    language === "ar"
      ? {
          open: `فتح ${title}`,
          watch: "شاهد المقطع",
          play: "شاهد هذه الشهادة",
          comingSoon: "المقطع قريبًا",
        }
      : {
          open: `Open ${title}`,
          watch: "Watch segment",
          play: "Play this certification",
          comingSoon: "Segment coming soon",
        };

  const handlePlay = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPlay();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={text.open}
      onClick={onToggle}
      onKeyDown={handleKeyDown}
      className="group block h-[390px] w-full cursor-pointer text-start [perspective:1200px]"
    >
      <div
        className={`relative h-full w-full transition-transform duration-700 [transform-style:preserve-3d] [transition-timing-function:cubic-bezier(0.2,0.75,0.2,1)] ${
          flipped ? "[transform:rotateY(180deg)]" : ""
        }`}
      >
        <div
          className="absolute inset-0 overflow-hidden rounded-[22px] border-t-4 bg-white shadow-[0_16px_45px_rgba(12,48,82,0.1)] [backface-visibility:hidden] [-webkit-backface-visibility:hidden]"
          style={{ borderTopColor: categoryColor }}
        >
          {credential.logo ? (
            <img
              src={credential.logo}
              alt={title}
              className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary/80">
              <span className="text-4xl font-bold text-white/90">
                {credential.code}
              </span>
            </div>
          )}

          <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-4">
            {primaryCategory && (
              <span
                title={categoryLabel}
                className="max-w-[62%] truncate rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold shadow-sm"
                style={{ color: categoryColor }}
              >
                {categoryLabel}
              </span>
            )}
            <span
              title={difficultyLabel}
              className="shrink-0 rounded-full bg-primary/90 px-2.5 py-1 text-[10px] font-bold text-white shadow-sm"
            >
              {credential.difficultyScore} · {difficultyLabel}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/95 via-primary/50 to-transparent p-5 pt-24 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#ffb59e]">
              {credential.code}
            </p>
            <h3 className="mt-2 text-lg font-bold">
              <BilingualText value={credential.name} language={language} />
            </h3>
            <button
              type="button"
              onClick={handlePlay}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-[#df4315]"
            >
              <Play size={13} fill="currentColor" />
              {text.watch}
            </button>
          </div>
        </div>

        <div
          className="absolute inset-0 flex flex-col justify-between rounded-[22px] border-t-4 bg-primary p-6 text-white [backface-visibility:hidden] [-webkit-backface-visibility:hidden] [transform:rotateY(180deg)]"
          style={{ borderTopColor: categoryColor }}
        >
          <div>
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={handlePlay}
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-secondary transition hover:bg-[#df4315]"
                aria-label={text.play}
              >
                <Play size={19} fill="currentColor" />
              </button>
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold text-white">
                {credential.difficultyScore} · {difficultyLabel}
              </span>
            </div>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#ffb59e]">
              {credential.code}
            </p>
            <h3 className="mt-2 text-xl font-bold">
              <BilingualText value={credential.name} language={language} />
            </h3>
            <p className="mt-3 line-clamp-4 text-sm leading-6 text-white/75">
              <BilingualText value={credential.summary} language={language} />
            </p>
          </div>
          <button
            type="button"
            onClick={handlePlay}
            disabled={!hasVideo}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/20 disabled:opacity-40"
          >
            <Play size={16} fill="currentColor" />
            {hasVideo ? text.play : text.comingSoon}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredentialLibraryCard;
